import {
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  ChangeEvent,
  InputHTMLAttributes,
  useState,
} from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import { InputText } from './InputText';
import { CloseIcon } from './icons';

export type InputSearchHandle = {
  reset: () => void;
  focus: () => void;
  value: string;
  disabled: boolean;
};
interface InputSearchProps extends InputHTMLAttributes<HTMLInputElement> {
  value?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  isLoading?: boolean;
  clearIconClassName?: string;
}

export const InputSearch = forwardRef<InputSearchHandle, InputSearchProps>(
  ({ onChange, onClear, isLoading, clearIconClassName, ...restProps }, ref) => {
    const internalInputSearchRef = useRef<HTMLInputElement>(null);
    const [internalSearchText, setInternalSearchText] = useState('');

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
      setInternalSearchText(event.target.value);
    };

    const handleClearInput = useCallback(() => {
      setInternalSearchText('');
      onChange?.({ target: { value: '' } } as ChangeEvent<HTMLInputElement>); // Simulate onChange for controlled component
      onClear?.();
      if (internalInputSearchRef.current) {
        internalInputSearchRef.current.value = '';
        internalInputSearchRef.current.focus();
      }
    }, [onChange, onClear]);

    // Expose reset method via ref
    useImperativeHandle(
      ref,
      () => ({
        reset: handleClearInput,
        originalInput: internalInputSearchRef.current,
        focus: () => internalInputSearchRef.current?.focus(),

        get value() {
          return internalInputSearchRef.current?.value || '';
        },
        set value(newValue: string) {
          if (internalInputSearchRef.current) {
            internalInputSearchRef.current.value = newValue;
            setInternalSearchText(newValue);
          }
        },
        get disabled() {
          return internalInputSearchRef.current?.disabled || false;
        },
        set disabled(newValue: boolean) {
          if (internalInputSearchRef.current) {
            internalInputSearchRef.current.disabled = newValue;
          }
        },
      }),
      [handleClearInput],
    );

    return (
      <div className={classNames('relative flex h-8 w-80 max-w-full min-w-0 items-center', restProps.className)}>
        <div
          className={classNames(
            'pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3',
            'text-fletchers-gray-200',
          )}
        >
          <i className="pi pi-search text-fletchers-gray-175 text-[1rem]" aria-hidden="true" />
        </div>
        <InputText
          ref={internalInputSearchRef}
          onChange={handleInputChange}
          // Browsers otherwise offer past searches in their own dropdown.
          autoComplete="off"
          {...restProps}
          className={classNames(
            restProps.className,
            'h-full w-full !pl-9 !text-[0.875rem] placeholder:!text-[0.875rem] [&]:px-7',
          )}
        />

        {internalSearchText && !isLoading && (
          <button
            type="button"
            onClick={handleClearInput}
            className={classNames(
              'absolute inset-y-0 right-0 flex items-center pr-3 focus:outline-none',
              'text-fletchers-gray-200 hover:text-fletchers-black',
            )}
          >
            <CloseIcon
              className={classNames('max-h-[10px] max-w-[10px] cursor-pointer text-[.7rem]', clearIconClassName)}
            />
          </button>
        )}

        {isLoading && (
          <div className={classNames('absolute right-0 flex items-center pr-3')}>
            <ProgressSpinner style={{ width: '12px', height: '12px' }} strokeWidth="4" animationDuration="1.5s" />
          </div>
        )}
      </div>
    );
  },
);

// Allow usage without ref
InputSearch.displayName = 'InputSearch';
